import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Typography, List, Avatar, Button, Progress, Spin } from 'antd';
import { BookOutlined, UserOutlined, CalendarOutlined, TrophyOutlined, CalendarOutlined as CalIcon } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import { MentorBookingResponse } from '../../types';
import dayjs from 'dayjs';

const { Title } = Typography;

const MenteeDashboard: React.FC = () => {
  const navigate = useNavigate();

  const { data: bookingsRes, isLoading } = useQuery({
    queryKey: ['mentee-dashboard', 'my-bookings'],
    queryFn: async () => {
      const r = await apiService.getMentorBookingsByMentee(1, 100);
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 2,
  });

  const bookings: MentorBookingResponse[] = bookingsRes?.data ?? [];
  const totalCount = bookingsRes?.totalCount ?? 0;
  const now = dayjs();
  const thisMonthStart = now.startOf('month');
  const thisMonthEnd = now.endOf('month');
  const completed = bookings.filter(b => b.status === 'Completed');
  const thisMonthSessions = bookings.filter(b => {
    const start = dayjs(b.startTime);
    return start.isAfter(thisMonthStart) && start.isBefore(thisMonthEnd) && b.status === 'Completed';
  });
  const uniqueMentors = new Set(bookings.filter(b => ['Confirmed', 'Completed'].includes(b.status)).map(b => b.mentorId)).size;
  const upcoming = bookings
    .filter(b => b.status === 'Confirmed' && dayjs(b.startTime).isAfter(now))
    .sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf())
    .slice(0, 5);

  const achievements = [
    { title: 'İlk Mentörlük Seansı', description: 'İlk mentörlük seansınızı tamamlayın', icon: '🎉', completed: completed.length >= 1, progress: completed.length >= 1 ? 100 : 0 },
    { title: '5 Seans Tamamlama', description: '5 mentörlük seansını tamamlayın', icon: '📚', completed: completed.length >= 5, progress: Math.min(100, (completed.length / 5) * 100) },
    { title: 'Proje Teslimi', description: 'Mentörünüzle birlikte bir proje tamamlayın', icon: '🚀', completed: false, progress: 30 },
  ];

  if (isLoading) {
    return (
      <div style={{ padding: '24px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--bg-tertiary)', minHeight: '100vh' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Mentee Dashboard
      </Title>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Seans"
              value={totalCount}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aktif Mentör"
              value={uniqueMentors}
              prefix={<UserOutlined />}
              valueStyle={{ color: 'var(--primary-500)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Bu Ay Seans"
              value={thisMonthSessions.length}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tamamlanan"
              value={completed.length}
              suffix={totalCount ? ` / ${totalCount}` : ''}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title="Yaklaşan Seanslar"
            extra={
              <Button type="link" onClick={() => navigate('/mentee-profile?tab=appointments')}>
                Tümünü Gör
              </Button>
            }
          >
            {upcoming.length === 0 ? (
              <div style={{ color: '#8c8c8c', textAlign: 'center', padding: 16 }}>
                Yaklaşan randevu yok. <Link to="/mentor-search">Mentör ara</Link> ile randevu oluşturabilirsiniz.
              </div>
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={upcoming}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<CalIcon />} />}
                      title={item.mentor ? `${item.mentor.firstName} ${item.mentor.lastName}` : 'Mentör'}
                      description={
                        <div>
                          <div>{dayjs(item.startTime).format('DD MMM YYYY, HH:mm')}</div>
                          <div style={{ color: '#666', fontSize: '12px' }}>{item.notes || 'Randevu'}</div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Başarılarım">
            <List
              itemLayout="horizontal"
              dataSource={achievements}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<div style={{ fontSize: '24px' }}>{item.icon}</div>}
                    title={item.title}
                    description={
                      <div>
                        <div>{item.description}</div>
                        {item.progress !== undefined && (
                          <Progress percent={item.progress} size="small" style={{ marginTop: '8px' }} />
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Hızlı İşlemler">
            <Row gutter={[8, 8]}>
              <Col xs={12}>
                <Button type="primary" block onClick={() => navigate('/mentor-search')}>
                  Mentor Ara
                </Button>
              </Col>
              <Col xs={12}>
                <Button block onClick={() => navigate('/mentee-profile?tab=appointments')}>
                  Randevularım
                </Button>
              </Col>
              <Col xs={12}>
                <Button block onClick={() => navigate('/mentee-profile?tab=profile')}>
                  Profil Güncelle
                </Button>
              </Col>
              <Col xs={12}>
                <Button block onClick={() => navigate('/mentee-profile?tab=job-applications')}>
                  İş Başvurularım
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Özet">
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Tamamlanan seans</span>
                <span>{completed.length}</span>
              </div>
              <Progress percent={totalCount ? Math.round((completed.length / totalCount) * 100) : 0} strokeColor="var(--success)" />
            </div>
            <Button type="link" onClick={() => navigate('/mentee-profile')} style={{ padding: 0 }}>
              Panele git →
            </Button>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MenteeDashboard;
